<template>
  <v-card
    class="pa-5 ml-10 mr-10 mt-5"
    :title="t('views.licence.table.title')">
    <template v-slot:append>
      <div class="paymentMode">
        <button data-cy="mode-monthly" @click="mode='monthly'" class="modeButton" :class="mode === 'monthly' ? 'active' : 'not-active'">
          {{ $t("views.licence.other.monthly") }}
          <small
            v-if="reviewData.pendingSwitch && reviewData.currentMode === 'monthly'"
            class="ml-1"
            data-cy="mode-current-badge">
            {{ $t("views.licence.other.currentUntil", { date: pendingDate }) }}
          </small>
        </button>
        <button data-cy="mode-yearly" @click="mode='yearly'" class="modeButton" :class="mode === 'yearly' ? 'active' : 'not-active'">
          {{ $t("views.licence.other.yearly") }}
          <small
            v-if="reviewData.pendingSwitch && reviewData.currentMode === 'yearly'"
            class="ml-1"
            data-cy="mode-current-badge">
            {{ $t("views.licence.other.currentUntil", { date: pendingDate }) }}
          </small>
        </button>
      </div>
    </template>
    <v-card-text>
      <v-data-table
        hide-default-footer
        items-per-page="20"
        class="border-table"
        density="compact"
        :headers="headers"
        hide-no-data
        item-value="id"
        :items="items">
        <template v-slot:item.active="{ item }">
          <v-checkbox
            :disabled="item.requires && (item.requires & tempResult) == 0"
            :data-cy="'module-' + item.id + '-active'"
            :true-value="1"
            :false-value="0"
            :model-value="item.slots"
            @update:model-value="($event) => changeValue(item.value, $event)"
            hide-details>
          </v-checkbox>

          <!-- module trial (access without booking): book it permanently -->
          <template v-if="trialOf(item.name) && !item.slots">
            <v-chip
              size="small"
              color="info"
              class="ml-2"
              :data-cy="'module-' + item.id + '-trial'">
              {{ $t("views.licence.promo.trialUntil", { date: new Date(trialOf(item.name).end_date).toLocaleDateString(), count: trialOf(item.name).quantity, interpolation: { escapeValue: false } }) }}
            </v-chip>
            <v-btn
              size="x-small"
              class="ml-2"
              :data-cy="'module-' + item.id + '-book-trial'"
              @click="changeValue(item.value, 1)">
              {{ $t("views.licence.promo.bookPermanently") }}
            </v-btn>
          </template>

          <span v-if="item.monthEnd" :data-cy="'module-' + item.id + '-until'"> {{$t("views.licence.review.until", {date: (new Date(props.next_billing_cycle_anchor * 1000)).toLocaleDateString()})}}</span>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-text>
      <v-row class="mt-5">
        <v-col
          cols="6"
          style="display: grid; justify-content: end"
          >{{ $t("views.licence.review." + mode) }}</v-col
        >
        <v-col
          cols="6"
          style="display: grid; justify-content: end">
          <s
            data-cy="old-monthly-costs"
            v-if="reviewData.oldCosts !== 0 && reviewData.oldCosts !== reviewData.newCosts"
            >{{ reviewData.oldCosts }} €</s
          >
          <span data-cy="final-monthly-costs">{{ reviewData.newCosts }} €</span>
        </v-col>
      </v-row>
      <v-row
        v-if="reviewData.discount"
        class="mt-0">
        <v-col
          cols="12"
          style="display: grid; justify-content: end">
          <DiscountLine :discount="reviewData.discount" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn @click="router.back()"> Zurück </v-btn>
      <v-btn
        data-cy="licence-save"
        :readonly="saving"
        @click="saveModules(() => save())">
        <template v-if="saving">
          <v-progress-circular indeterminate />
        </template>
        <template v-else>Speichern</template>
      </v-btn>
    </v-card-actions>
  </v-card>
  <v-dialog
    v-model="dialog.open"
    persistent>
    <LicenceReviewPopup
      private
      @close="dialog.open = false"
      @save="submit"
      :review-data="reviewData"
      :initial-items="initialItems"
      :items="items"
      :charge-needed="chargeNeeded"
      :modules-removed="modulesRemoved" />
  </v-dialog>
</template>

<script lang="ts" setup>
import { useConfirm, useSnackbar } from "vuetify-use-dialog";
import { computed, ModelRef, PropType, Ref, ref, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import { useMainStore } from "@/stores/mainStore";
import { useModules, type PromoInfo } from "./moduleComposable";
import DiscountLine from "./DiscountLine.vue";
import LicenceReviewPopup from "@/components/dialogs/LicenceReviewPopup.vue";
import { useRouter } from "vue-router";
import { ComputedRef } from "vue";
import { phase } from "@/types/global";

const createConfirm = useConfirm();
const createSnackbar = useSnackbar();
const { t } = useTranslation();
const mainStore = useMainStore();

const dialog = ref({
  open: false,
  onSuccess: () => {}
});

const emit = defineEmits(["moduleUpdate"]);
const props = defineProps({
  current_phase: Object,
  current_phase_mode: String,
  next_phase: Object,
  save: Function,
  saving: Boolean,
  current_status: String,
  licenceId: Number,
  next_billing_cycle_anchor: Number,
  next_phase_mode: String,
  promoCode: String,
  promoInfo: Object as PropType<PromoInfo | null>,
  activeDiscount: Object as PropType<PromoInfo | null>,
  trials: Array as PropType<{ module: string; quantity: number; end_date: string }[]>
});

const model = defineModel<Record<number, number>>();
const mode = defineModel<string>("mode");

defineExpose({
  saveModules
});

const {
  headers,
  items,
  modulesRemoved,
  chargeNeeded,
  initialItems,
  loadPreview,
  previewNeeded,
  changeNeeded,
  reviewData,
} = useModules(
  toRef(() => model.value),
  true,
  toRef(() => props.current_status),
  toRef(() => props.licenceId),
  toRef(() => mode.value),
  toRef(() => props.current_phase) as Ref<phase>,
  toRef(() => props.next_phase) as Ref<phase>,
  props.current_phase_mode,
  props.next_billing_cycle_anchor as number,
  props.next_phase_mode,
  toRef(() => props.promoCode),
  toRef(() => props.promoInfo),
  toRef(() => props.activeDiscount)
);

const trialOf = (name: string) =>
  props.trials?.find((trial) => trial.module === name);

const pendingDate = computed(() =>
  new Date((props.next_billing_cycle_anchor ?? 0) * 1000).toLocaleDateString()
);

const tempResult: ComputedRef<number> = computed(() => {
  return Object.entries(model.value as Record<number, number>).reduce((result, [key, value]) => {
    return value ? result | Number(key) : result;
  }, 0);
});

const modulesRequirements = computed(() => {
  return Object.fromEntries(
    mainStore.modules.map((item) => [item.value, item.requires])
  );
});

function changeValue(item: number, value: number) {
  console.log(value);
  model.value![item] = value;
  if (value === 0) {
    console.log(item);
    for (const [id, requires] of Object.entries(modulesRequirements.value)) {
      if (item & (requires || 0)) {
        changeValue(id as unknown as number, 0);
      }
    }
  }
}

const router = useRouter();

function saveModules(callback: () => void) {
  if (!changeNeeded.value) {
    router.back();
    return;
  }
  dialog.value.onSuccess = callback;
  dialog.value.open = true;
  previewNeeded.value && loadPreview();
}

function submit() {
  dialog.value.onSuccess();
}
</script>

<style lang="scss" scoped>
.paymentMode {
  border-radius: 30px;
  background-color: #eeeeee;
  padding: 3px;

  .modeButton {
    border-radius: 25px;
    padding: 7px 20px;
    margin: 1px 4px;
    color: #666666;
  }
  @keyframes onHover {
    0%   {color: #666666;}
    100%  {color: black;}
  }
  @keyframes onLeave {
    0%   {color: black;}
    100%  {color: #666666;}
  }
  .not-active {
    animation: onLeave 0.5s;

    &:hover {
      color: black;
      animation: onHover 0.5s;
    }
  }

  .active {
    background-color: white;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 3px 10px 0 rgba(0, 0, 0, 0.19);
    color: black;
  }
}
</style>
